import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminHeader from '../Layout/AdminHeader';
import AdminSidebar from '../Layout/AdminSidebar';
import PageInstructionBanner from '../common/PageInstructionBanner';
import '../Dashboard/Dashboard.css';
import './RegistrationWizard.css';
import { getEventBySlug, registerForEvent } from '../../services/events';
import EditProfileResume from '../Dashboard/EditProfileResume';
import SurveyForm from '../Dashboard/SurveyForm';
import { useAuth } from '../../contexts/AuthContext';
import MyAccountInline from '../Account/MyAccountInline';
import { termsConditionsAPI } from '../../services/termsConditions';
import ResumeSelectWidget from '../Dashboard/ResumeBuilder/ResumeSelectWidget';

/** Strip HTML to plain text for reliable screen-reader descriptions. */
function htmlToPlainText(html) {
  if (!html) return '';
  return String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
}

export default function RegistrationWizard() {
  const { slug } = useParams();
  const { user, loading, updateProfile } = useAuth();
  const [event, setEvent] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState({});
  const [announcementsOptIn, setAnnouncementsOptIn] = useState(!!user?.subscribeAnnouncements);
  const [termsDocuments, setTermsDocuments] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [isAlreadyRegistered, setIsAlreadyRegistered] = useState(false);
  const [stepValidation, setStepValidation] = useState({
    step1: false,
    step2: false,
    step3: false
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [stepAttempted, setStepAttempted] = useState({ 1: false, 2: false, 3: false });
  const [currentFormData, setCurrentFormData] = useState(null);
  const [step1FormData, setStep1FormData] = useState(null);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [resumeSource, setResumeSource] = useState('upload'); // 'upload' | 'saved'
  const navigate = useNavigate();
  const location = useLocation();
  const liveRef = useRef(null);
  const stepHeadingRef = useRef(null);
  const surveyFormRef = useRef(null);
  const pageHeadingRef = useRef(null);
  const resumeSourceGroupRef = useRef(null);
  const termsSectionRef = useRef(null);
  const registeredEventsKey = useMemo(
    () => JSON.stringify(user?.metadata?.registeredEvents || []),
    [user?.metadata?.registeredEvents]
  );

  // Keep keyboard focus aligned with the currently visible registration content.
  // This also handles initial async page load before step content mounts.
  useEffect(() => {
    if (fetching) return;
    if (stepHeadingRef.current) {
      stepHeadingRef.current.setAttribute('tabindex', '-1');
      stepHeadingRef.current.focus();
      return;
    }
    const focusTarget = document.getElementById('main-content')
      || document.querySelector('main');
    if (focusTarget) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus();
    }
  }, [step, fetching, isAlreadyRegistered]);

  // Check if user is already registered for this event
  const checkRegistrationStatus = (event, user) => {
    if (!event || !user) return false;
    const registeredEvents = user.metadata?.registeredEvents || [];
    return registeredEvents.some(reg =>
      (reg.id && reg.id.toString() === event._id?.toString()) ||
      (reg.slug && reg.slug === event.slug)
    );
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    (async () => {
      try {
        const res = await getEventBySlug(slug);
        console.log('Event data:', res?.event); // Debug log
        const eventData = res?.event || null;
        setEvent(eventData);

        // Check if already registered
        if (eventData && user) {
          const alreadyRegistered = checkRegistrationStatus(eventData, user);
          setIsAlreadyRegistered(alreadyRegistered);
          console.log('Already registered:', alreadyRegistered); // Debug log
        }

        // Fetch terms documents if event has termsIds
        if (res?.event?.termsIds?.length) {
          console.log('Event has termsIds:', res.event.termsIds); // Debug log
          setLoadingTerms(true);
          try {
            const termsPromises = res.event.termsIds.map(id =>
              termsConditionsAPI.getById(id).then(response => {
                console.log(`Terms document ${id}:`, response); // Debug log
                // Handle backend response structure: { terms: { ... } }
                if (response.terms) {
                  return response.terms;
                } else if (response.data?.terms) {
                  return response.data.terms;
                } else if (response._id || response.id) {
                  return response;
                } else {
                  console.warn(`Unexpected response structure for terms ${id}:`, response);
                  return { _id: id, title: `Terms Document ${id}`, content: 'Unable to load terms content.', isRequired: true };
                }
              }).catch(err => {
                console.warn(`Failed to fetch terms ${id}:`, err);
                return { _id: id, title: `Terms Document ${id}`, content: 'Unable to load terms content.', isRequired: true };
              })
            );
            const terms = await Promise.all(termsPromises);
            console.log('All terms documents:', terms); // Debug log
            setTermsDocuments(terms.filter(Boolean));
          } catch (error) {
            console.error('Error fetching terms:', error);
          } finally {
            setLoadingTerms(false);
          }
        } else {
          console.log('Event has no termsIds or empty array'); // Debug log
          // Try to fetch active terms as fallback
          try {
            const activeTermsResponse = await termsConditionsAPI.getActive();
            console.log('Active terms fallback:', activeTermsResponse); // Debug log
            // Handle response structure: { terms: { ... } }
            if (activeTermsResponse?.terms) {
              setTermsDocuments([activeTermsResponse.terms]);
            } else if (activeTermsResponse?.data?.terms) {
              setTermsDocuments([activeTermsResponse.data.terms]);
            }
          } catch (error) {
            console.warn('Failed to fetch active terms:', error);
          }
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      } finally {
        setFetching(false);
      }
    })();
  }, [slug, user?._id, registeredEventsKey, loading]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/register?redirect=${redirect}`, { replace: true });
    }
  }, [user, loading, location, navigate]);

  useEffect(() => {
    if (liveRef.current) {
      const eventName = event?.name || 'Event';
      const stepAnnouncements = [
        `${eventName} Registration - Page 1 of 3`,
        `${eventName} Registration Page 2 of 3 - Please complete all required fields`,
        `${eventName} Registration Page 3 of 3 – Survey and Agreements`
      ];
      liveRef.current.textContent = stepAnnouncements[step - 1];
    }
    // Scroll to top whenever step changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Also scroll main content area if it exists
    const mainContent = document.getElementById('dashboard-main') || document.querySelector('.dashboard-main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  // Announce validation errors only after a failed continue on this visit.
  // Do not replay errors just because the user navigated back to a completed step.
  useEffect(() => {
    if (!liveRef.current) return;
    if (!stepAttempted[step]) return;
    const currentStepErrors = validationErrors[`step${step}`];
    if (currentStepErrors && Object.keys(currentStepErrors).length > 0) {
      const errorMessages = Object.values(currentStepErrors).join(', ');
      liveRef.current.textContent = `Validation errors: ${errorMessages}`;
    }
  }, [validationErrors, step, stepAttempted]);

  const prevResumeSourceRef = useRef(resumeSource);
  useEffect(() => {
    if (step !== 2 || !liveRef.current) return;
    if (prevResumeSourceRef.current === resumeSource) return;
    prevResumeSourceRef.current = resumeSource;
    liveRef.current.textContent = resumeSource === 'saved'
      ? 'Saved resume selected. Choose a resume from your Resume Builder list.'
      : 'Upload resume selected. Choose a file from your device to continue.';
  }, [resumeSource, step]);

  const allTermsAccepted = useMemo(() => {
    if (!termsDocuments.length) {
      return termsAccepted.general === true;
    }
    const requiredTerms = termsDocuments.filter(doc => doc.isRequired !== false);
    return requiredTerms.every(doc => {
      const docId = doc._id || doc.id;
      return termsAccepted[docId] === true;
    });
  }, [termsDocuments, termsAccepted]);

  const focusFirstInvalid = (stepNumber, errors) => {
    if (!errors || !Object.keys(errors).length) return;
    setTimeout(() => {
      if (stepNumber === 1) {
        const order = ['state', 'city', 'country'];
        const first = order.find((field) => errors[field]);
        if (first) {
          const el = document.getElementById(first);
          if (el) {
            el.focus();
            return;
          }
        }
      }
      if (stepNumber === 2) {
        const profileOrder = [
          'headline', 'keywords', 'primaryExperience', 'workLevel',
          'educationLevel', 'employmentTypes', 'languages'
        ];
        const first = profileOrder.find((field) => errors[field]);
        if (first) {
          const original = document.getElementById(first);
          if (original) {
            const wrapper = original.closest('.e-multiselect');
            const target = wrapper
              ? (wrapper.querySelector('input.e-dropdownbase, input[role="combobox"]') || original)
              : original;
            if (target && typeof target.focus === 'function') {
              target.focus();
              return;
            }
          }
        }
        if (errors.resume && resumeSourceGroupRef.current) {
          resumeSourceGroupRef.current.setAttribute('tabindex', '-1');
          resumeSourceGroupRef.current.focus();
        }
        return;
      }
      if (stepNumber === 3 && errors.terms) {
        const checkbox = termsSectionRef.current?.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.focus();
          return;
        }
        if (termsSectionRef.current) {
          termsSectionRef.current.setAttribute('tabindex', '-1');
          termsSectionRef.current.focus();
        }
      }
    }, 60);
  };

  // Validation functions for each step
  const validateStep1 = (formDataOverride = null) => {
    if (!user) return false;
    const errors = {};
    let isValid = true;

    // Use formDataOverride if provided (from form submission), then step1FormData, then user object
    const city = formDataOverride?.city || step1FormData?.city || user.city || '';
    const state = formDataOverride?.state || step1FormData?.state || user.state || '';
    const country = formDataOverride?.country || step1FormData?.country || user.country || '';

    // Required fields for step 1: city, state, country
    if (!city || city.trim() === '') {
      errors.city = 'City is required';
      isValid = false;
    }
    if (!state || state.trim() === '') {
      errors.state = 'State is required';
      isValid = false;
    }
    if (!country || country.trim() === '') {
      errors.country = 'Country is required';
      isValid = false;
    }

    setValidationErrors(prev => ({ ...prev, step1: errors }));
    setStepValidation(prev => ({ ...prev, step1: isValid }));
    return isValid;
  };

  const validateStep2 = () => {
    const errors = {};
    let isValid = true;

    // All fields are required for step 2
    const requiredFields = [
      'headline', 'keywords', 'primaryExperience', 'workLevel',
      'educationLevel', 'languages', 'employmentTypes'
    ];

    if (!currentFormData) {
      requiredFields.forEach(field => {
        errors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
      });
      errors.resume = resumeSource === 'saved'
        ? 'Please select a saved resume or switch to upload a file'
        : 'Resume upload is required';
      setValidationErrors(prev => ({ ...prev, step2: errors }));
      setStepValidation(prev => ({ ...prev, step2: false }));
      return false;
    }

    requiredFields.forEach(field => {
      const value = currentFormData[field];

      if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
        errors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
        isValid = false;
      }
    });

    // Check resume: either an uploaded file OR a selected saved resume satisfies the requirement
    const hasResumeInForm = currentFormData.hasResume || currentFormData.resumeUrl;
    const hasResumeInUser = user?.resumeUrl;
    const hasSelectedResume = resumeSource === 'saved' && !!selectedResumeId;

    if (!hasResumeInForm && !hasResumeInUser && !hasSelectedResume) {
      errors.resume = resumeSource === 'saved'
        ? 'Please select a saved resume or switch to upload a file'
        : 'Resume upload is required';
      isValid = false;
    }

    setValidationErrors(prev => ({ ...prev, step2: errors }));
    setStepValidation(prev => ({ ...prev, step2: isValid }));
    return isValid;
  };

  const validateStep3 = () => {
    if (!user) return false;
    const errors = {};
    let isValid = true;

    // Check terms acceptance
    if (!allTermsAccepted) {
      errors.terms = 'Terms and conditions acceptance is required';
      isValid = false;
    }

    setValidationErrors(prev => ({ ...prev, step3: errors }));
    setStepValidation(prev => ({ ...prev, step3: isValid }));
    return isValid;
  };

  // After a failed continue, re-validate so corrected fields clear from the summary.
  // Skip when returning to this step: the profile form remounts empty before it
  // reloads, and validating that empty state would flash a false error banner.
  useEffect(() => {
    if (step === 2 && stepAttempted[2] && currentFormData) {
      validateStep2();
    }
  }, [currentFormData, resumeSource, selectedResumeId, step, stepAttempted]);

  useEffect(() => {
    if (step === 3 && stepAttempted[3]) {
      validateStep3();
    }
  }, [allTermsAccepted, step, stepAttempted]);

  const scrollStepToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const mainContent = document.getElementById('dashboard-main') || document.querySelector('.dashboard-main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToStep = (target) => {
    const nextStep = Math.min(3, Math.max(1, target));
    if (nextStep < step) {
      // Returning to a completed step must not replay validation errors.
      setStepAttempted(prev => ({ ...prev, [nextStep]: false }));
    }
    setStep(nextStep);
    scrollStepToTop();
  };

  const next = (formDataOverride = null) => {
    let canProceed = false;
    let errors = {};

    switch (step) {
      case 1:
        canProceed = validateStep1(formDataOverride);
        if (!canProceed) {
          const city = formDataOverride?.city || step1FormData?.city || user?.city || '';
          const state = formDataOverride?.state || step1FormData?.state || user?.state || '';
          const country = formDataOverride?.country || step1FormData?.country || user?.country || '';
          if (!city || city.trim() === '') errors.city = 'City is required';
          if (!state || state.trim() === '') errors.state = 'State is required';
          if (!country || country.trim() === '') errors.country = 'Country is required';
        }
        break;
      case 2:
        canProceed = validateStep2();
        if (!canProceed) {
          errors = {}; // focusFirstInvalid will read from state asynchronously; compute below
        }
        break;
      case 3:
        canProceed = validateStep3();
        break;
      default:
        canProceed = false;
    }

    if (canProceed) {
      // Successful continue is not a failed attempt — returning later must not
      // show or announce the previous validation state.
      setStepAttempted(prev => ({ ...prev, [step]: false }));
      setStep(s => Math.min(3, s + 1));
      setValidationErrors(prev => ({ ...prev, [`step${step}`]: {} }));
      scrollStepToTop();
      return true;
    }

    setStepAttempted(prev => ({ ...prev, [step]: true }));

    // Focus first invalid control after a failed continue attempt
    if (step === 1) {
      focusFirstInvalid(1, errors);
    } else if (step === 2) {
      // Recompute step 2 errors for focus (validateStep2 already set state)
      const step2Errors = {};
      if (currentFormData) {
        const requiredFields = [
          'headline', 'keywords', 'primaryExperience', 'workLevel',
          'educationLevel', 'languages', 'employmentTypes'
        ];
        requiredFields.forEach(field => {
          const value = currentFormData[field];
          if (!value || (Array.isArray(value) && value.length === 0) || (typeof value === 'string' && value.trim() === '')) {
            step2Errors[field] = true;
          }
        });
        const hasResumeInForm = currentFormData.hasResume || currentFormData.resumeUrl;
        const hasResumeInUser = user?.resumeUrl;
        const hasSelectedResume = resumeSource === 'saved' && !!selectedResumeId;
        if (!hasResumeInForm && !hasResumeInUser && !hasSelectedResume) {
          step2Errors.resume = true;
        }
      } else {
        step2Errors.resume = true;
      }
      focusFirstInvalid(2, step2Errors);
    } else if (step === 3) {
      focusFirstInvalid(3, { terms: true });
    }
    return false;
  };

  const prev = () => {
    goToStep(step - 1);
  };

  const handleAnnouncementsToggle = async (checked) => {
    setAnnouncementsOptIn(checked);
    try {
      await updateProfile({ subscribeAnnouncements: !!checked });
    } catch { }
  };

  const handleComplete = async () => {
    setStepAttempted(prev => ({ ...prev, 3: true }));
    if (!validateStep3()) {
      focusFirstInvalid(3, { terms: true });
      return;
    }
    setSaving(true);
    try {
      const surveySaved = await surveyFormRef.current?.save();
      if (!surveySaved) {
        return;
      }
      if (liveRef.current) {
        liveRef.current.textContent = '';
        setTimeout(() => {
          if (liveRef.current) {
            liveRef.current.textContent = 'Survey saved. Completing registration.';
          }
        }, 30);
      }
      const mainContent = document.getElementById('main-content') || document.querySelector('main');
      if (mainContent) {
        mainContent.setAttribute('tabindex', '-1');
        mainContent.focus();
      }

      const termsAcceptanceData = {
        acceptedTerms: termsAccepted,
        acceptedAt: new Date().toISOString(),
        eventSlug: slug
      };

      // Update user profile with terms acceptance
      await updateProfile({
        termsAcceptance: termsAcceptanceData,
        subscribeAnnouncements: announcementsOptIn
      });

      // Register for the event (optionally attach a resume from Resume Builder)
      await registerForEvent(slug, { resumeId: selectedResumeId || undefined });

      // Navigate directly to this registration detail view.
      const registeredEventKey = encodeURIComponent(event?.slug || event?._id || slug);
      navigate(`/events/registered/${registeredEventKey}`, { replace: true });
    } catch (e) {
      console.error('Registration error:', e);
      // no-op; could surface toast later
    } finally {
      setSaving(false);
    }
  };

  const handleTermsAcceptance = (termId, accepted) => {
    setTermsAccepted(prev => ({
      ...prev,
      [termId]: accepted
    }));
  };

  const showStep2ResumeError = stepAttempted[2] && !!validationErrors.step2?.resume;
  const showStep3TermsError = stepAttempted[3] && !!validationErrors.step3?.terms;

  const termsBlocks = useMemo(() => {
    if (!termsDocuments.length) return null;
    return (
      <div className="terms-documents">
        {termsDocuments.map(doc => {
          const docId = doc._id || doc.id;
          const docTitle = doc.title || doc.name || 'Terms & Conditions';
          const docContent = doc.content || doc.description || '';
          const plainContent = htmlToPlainText(docContent)
            || 'Terms content not available. Please contact support to review the full terms and conditions for this event.';
          const isRequired = doc.isRequired !== false;
          const checkboxId = `terms-check-${docId}`;
          const labelId = `terms-label-${docId}`;
          const contentId = `terms-content-${docId}`;
          const statusId = `terms-status-${docId}`;
          // Name includes the full term text first so JAWS/NVDA always read it on focus.
          // Status/required stays in describedby so checking does not announce "checked required".
          const labelledBy = `${contentId} ${labelId}`;
          const describedBy = [
            statusId,
            showStep3TermsError && isRequired ? 'terms-error' : null,
          ].filter(Boolean).join(' ');

          return (
            <div key={docId} className="terms-document-option" data-required={isRequired}>
              <div className="terms-content-preview">
                {/* Visible, non-scrollable text so aria-labelledby always exposes the full term. */}
                <p id={contentId} className="terms-agreement-body">{plainContent}</p>
              </div>
              <div className="terms-checkbox-wrapper">
                <label className="checkbox-label" htmlFor={checkboxId}>
                  <input
                    id={checkboxId}
                    type="checkbox"
                    checked={termsAccepted[docId] || false}
                    onChange={(e) => handleTermsAcceptance(docId, e.target.checked)}
                    className="checkbox-input"
                    aria-labelledby={labelledBy}
                    aria-describedby={describedBy || undefined}
                    aria-invalid={showStep3TermsError && isRequired ? 'true' : undefined}
                  />
                  <span className="checkbox-custom" aria-hidden="true"></span>
                  <span className="checkbox-text">
                    <strong id={labelId}>I agree to {docTitle}</strong>
                    <small id={statusId}>
                      {isRequired ? 'Required to complete registration' : 'Optional'}
                    </small>
                  </span>
                </label>
              </div>
            </div>
          );
        })}
        {showStep3TermsError && (
          <div id="terms-error" className="field-error" role="alert" aria-live="polite">
            {validationErrors.step3.terms}
          </div>
        )}
      </div>
    );
  }, [termsDocuments, termsAccepted, showStep3TermsError, validationErrors.step3?.terms]);

  if (loading || fetching) {
    return <div className="dashboard"><a href="#main-content" className="skip-link">Skip to main content</a><div className="dashboard-layout"><main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content"><div>Loading…</div></main></div></div>;
  }
  if (!user) return null;

  const stepNames = ['My Account', 'Profile & Resume', 'Survey & Terms'];
  const pageTitles = event?.name
    ? [
        `${event.name} Registration - Page 1 of 3 - abilityconnect`,
        `${event.name} Registration Page 2 of 3 - Please complete all required fields - abilityconnect`,
        `${event.name} Registration Page 3 of 3 – Survey and Agreements - abilityconnect`
      ]
    : [
        `Event Registration - Page 1 of 3 - abilityconnect`,
        `Event Registration - Page 2 of 3 - abilityconnect`,
        `Event Registration - Page 3 of 3 - abilityconnect`
      ];
  const pageTitle = pageTitles[step - 1];

  return (
    <div className="dashboard">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <AdminHeader />
      <div className="dashboard-layout">
        <AdminSidebar active="events" />
        <main id="main-content" className="dashboard-main" tabIndex={-1} aria-label="main content">
          <div className="dashboard-content">
            <h1 ref={pageHeadingRef} style={{ textAlign: 'center', marginBottom: '1.5rem' }}>{event?.name || 'Event Registration'}</h1>
            <PageInstructionBanner screen="event-registration" />
            {isAlreadyRegistered ? (
              <div className="alert-box" style={{ background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' }}>
                <p><strong>You are already registered for this event.</strong> You can view your registration details in "My Current Registrations".</p>
                <div style={{ marginTop: '1rem' }}>
                  <button
                    className="ajf-btn ajf-btn-dark"
                    onClick={() => navigate(`/events/registered/${encodeURIComponent(event.slug || event._id)}`)}
                    style={{ marginRight: '0.5rem' }}
                  >
                    View My Registration
                  </button>
                  <button
                    className="ajf-btn ajf-btn-outline"
                    onClick={() => navigate('/events/registered')}
                  >
                    My Current Registrations
                  </button>
                </div>
              </div>
            ) : (
              <div className="alert-box"><p>Continue Registration by editing and submitting all three pages of registration.</p></div>
            )}

            <nav aria-label="Registration steps" className="wizard-nav">
              <div className="wizard-progress" role="progressbar" aria-valuenow={step} aria-valuemin="1" aria-valuemax="3" aria-label={`Step ${step} of 3`}>
                <div className="wizard-progress-bar" style={{ width: `${(step / 3) * 100}%` }}></div>
              </div>
              <ol className="wizard-steps">
                <li className={`wizard-step ${step >= 1 ? 'completed' : ''} ${step === 1 ? 'active' : ''}`}>
                  <button
                    className="wizard-step-btn"
                    aria-current={step === 1 ? 'step' : undefined}
                    onClick={() => goToStep(1)}
                    disabled={step < 1 || isAlreadyRegistered}
                    aria-label="Step 1: My Account - Personal information"
                  >
                    <span className="wizard-step-number" aria-hidden="true">1</span>
                    <div className="wizard-step-content">
                      <span className="wizard-step-title">My Account</span>
                      <span className="wizard-step-desc">Personal information</span>
                    </div>
                  </button>
                </li>
                <li className={`wizard-step ${step >= 2 ? 'completed' : ''} ${step === 2 ? 'active' : ''}`}>
                  <button
                    className="wizard-step-btn"
                    aria-current={step === 2 ? 'step' : undefined}
                    onClick={() => goToStep(2)}
                    disabled={step < 2 || isAlreadyRegistered}
                    aria-label="Step 2: Profile & Resume - Professional details"
                  >
                    <span className="wizard-step-number" aria-hidden="true">2</span>
                    <div className="wizard-step-content">
                      <span className="wizard-step-title">Profile & Resume</span>
                      <span className="wizard-step-desc">Professional details</span>
                    </div>
                  </button>
                </li>
                <li className={`wizard-step ${step >= 3 ? 'completed' : ''} ${step === 3 ? 'active' : ''}`}>
                  <button
                    className="wizard-step-btn"
                    aria-current={step === 3 ? 'step' : undefined}
                    onClick={() => goToStep(3)}
                    disabled={step < 3 || isAlreadyRegistered}
                    aria-label="Step 3: Survey & Terms - Final requirements"
                  >
                    <span className="wizard-step-number" aria-hidden="true">3</span>
                    <div className="wizard-step-content">
                      <span className="wizard-step-title">Survey & Terms</span>
                      <span className="wizard-step-desc">Final requirements</span>
                    </div>
                  </button>
                </li>
              </ol>
              <div aria-live="polite" ref={liveRef} className="sr-only" />
            </nav>

            {step === 1 && (
              <section aria-labelledby="account-h">
                <h3 id="account-h" ref={stepHeadingRef}>{event?.name || 'Event'} Registration - Page 1 of 3</h3>
                {stepAttempted[1] && validationErrors.step1 && Object.keys(validationErrors.step1).length > 0 && (
                  <div className="alert-box" style={{ background: '#fdecea', borderColor: '#f5c2c7', color: '#721c24' }} role="alert" aria-live="polite">
                    <h4>Please complete all required fields:</h4>
                    <ul>
                      {Object.entries(validationErrors.step1).map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <MyAccountInline
                  embedded
                  user={user}
                  updateProfile={updateProfile}
                  showErrors={stepAttempted[1]}
                  externalErrors={validationErrors.step1 || {}}
                  onDone={(formData) => {
                    // Store the form data for future reference
                    if (formData) {
                      setStep1FormData(formData);
                    }
                    // Pass formData directly to next() so validation uses it immediately
                    return next(formData);
                  }}
                />
              </section>
            )}

            {step === 2 && (
              <section aria-labelledby="profile-h">
                <h3 id="profile-h" ref={stepHeadingRef}>{event?.name || 'Event'} Registration Page 2 of 3 - Please complete all required fields</h3>
                {stepAttempted[2] && validationErrors.step2 && Object.keys(validationErrors.step2).length > 0 && (
                  <div className="alert-box" style={{ background: '#fdecea', borderColor: '#f5c2c7', color: '#721c24' }} role="alert" aria-live="polite">
                    <h4>Please complete all required fields:</h4>
                    <ul>
                      {Object.entries(validationErrors.step2).map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <EditProfileResume
                  embedded
                  resumeOptional={resumeSource === 'saved'}
                  resumeTopSlot={
                    <div className="resume-source-wrapper">
                      <section
                        ref={resumeSourceGroupRef}
                        className="resume-source-group"
                        aria-labelledby="resume-source-heading"
                        aria-describedby={`resume-source-hint ${showStep2ResumeError ? 'resume-source-error' : ''}`.trim()}
                      >
                        <h4 id="resume-source-heading" className="resume-source-heading">
                          Resume *
                        </h4>
                        <p id="resume-source-hint" className="resume-source-hint">
                          Choose the method you would like to add your resume.
                        </p>
                        <div
                          className="resume-source-options"
                          role="radiogroup"
                          aria-labelledby="resume-source-heading"
                          aria-describedby={`resume-source-hint ${showStep2ResumeError ? 'resume-source-error' : ''}`.trim()}
                        >
                          <label className={`resume-source-option ${resumeSource === 'upload' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="resumeSource"
                              value="upload"
                              checked={resumeSource === 'upload'}
                              onChange={() => setResumeSource('upload')}
                              aria-label="Upload Resume from File"
                              aria-describedby="resume-source-upload-desc"
                            />
                            <span>Upload Resume from File</span>
                            <span id="resume-source-upload-desc" className="sr-only">
                              Upload a PDF, DOC, or DOCX resume file from your device.
                            </span>
                          </label>
                          <label className={`resume-source-option ${resumeSource === 'saved' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name="resumeSource"
                              value="saved"
                              checked={resumeSource === 'saved'}
                              onChange={() => setResumeSource('saved')}
                              aria-label="Add from Resume Builder"
                              aria-describedby="resume-source-saved-desc"
                            />
                            <span>Add from Resume Builder</span>
                            <span id="resume-source-saved-desc" className="sr-only">
                              Use a resume created in Resume Builder.
                            </span>
                          </label>
                        </div>
                        {showStep2ResumeError && (
                          <div id="resume-source-error" className="field-error" role="alert" aria-live="polite">
                            {validationErrors.step2.resume}
                          </div>
                        )}
                      </section>
                      {resumeSource === 'saved' && (
                        <ResumeSelectWidget
                          selectedResumeId={selectedResumeId}
                          onChange={setSelectedResumeId}
                          skipLabel="Use no saved resume for this registration"
                          description="Select a saved resume to share with recruiters at this event."
                        />
                      )}
                    </div>
                  }
                  onValidationChange={(opts) => {
                    if (opts?.attempted) {
                      setStepAttempted(prev => ({ ...prev, 2: true }));
                      validateStep2();
                      return;
                    }
                    if (stepAttempted[2]) {
                      validateStep2();
                    }
                  }}
                  onFormDataChange={setCurrentFormData}
                  onDone={next}
                  onPrev={prev}
                />
              </section>
            )}

            {step === 3 && (
              <section aria-labelledby="survey-h">
                <h3 id="survey-h" ref={stepHeadingRef}>{event?.name || 'Event'} Registration Page 3 of 3 – Survey and Agreements</h3>
                {stepAttempted[3] && validationErrors.step3 && Object.keys(validationErrors.step3).length > 0 && (
                  <div className="alert-box" style={{ background: '#fdecea', borderColor: '#f5c2c7', color: '#721c24' }} role="alert" aria-live="polite">
                    <h4>Please complete all required fields:</h4>
                    <ul>
                      {Object.entries(validationErrors.step3).map(([field, error]) => (
                        <li key={field}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <SurveyForm
                  ref={surveyFormRef}
                  embedded
                  onValidationChange={() => {
                    if (stepAttempted[3]) {
                      validateStep3();
                    }
                  }}
                />
                <hr />
                <fieldset className="terms-fieldset" ref={termsSectionRef}>
                  <legend className="terms-section-title">Agreements to Terms and Privacy Policy (Required)</legend>
                  {loadingTerms ? (
                    <div className="terms-loading">Loading terms and conditions...</div>
                  ) : termsBlocks ? (
                    termsBlocks
                  ) : (
                    <div className="terms-fallback">
                      <div className="terms-document-option" data-required="true">
                        <div className="terms-content-preview">
                          <p id="terms-fallback-content" className="terms-agreement-body">
                            Terms content not available. By proceeding with registration, you agree to our Terms of Service and Privacy Policy. Please contact support if you need to review the full terms and conditions.
                          </p>
                        </div>
                        <div className="terms-checkbox-wrapper">
                          <label className="checkbox-label" htmlFor="terms-check-general">
                            <input
                              id="terms-check-general"
                              type="checkbox"
                              checked={termsAccepted.general || false}
                              onChange={(e) => {
                                handleTermsAcceptance('general', e.target.checked);
                              }}
                              className="checkbox-input"
                              aria-labelledby="terms-fallback-content terms-fallback-label"
                              aria-describedby={`terms-fallback-status${showStep3TermsError ? ' terms-error' : ''}`}
                              aria-invalid={showStep3TermsError ? 'true' : undefined}
                            />
                            <span className="checkbox-custom" aria-hidden="true"></span>
                            <span className="checkbox-text">
                              <strong id="terms-fallback-label">I accept the Terms & Conditions</strong>
                              <small id="terms-fallback-status">Required to complete registration</small>
                            </span>
                          </label>
                          {showStep3TermsError && (
                            <div id="terms-error" className="field-error" role="alert" aria-live="polite">
                              {validationErrors.step3.terms}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </fieldset>

                <fieldset className="announcements-fieldset">
                  <legend className="announcements-section-title">Communication Preferences</legend>
                  <div className="announcements-option">
                    <label className="checkbox-label" htmlFor="announcements-opt-in">
                      <input
                        id="announcements-opt-in"
                        type="checkbox"
                        checked={announcementsOptIn}
                        onChange={(e) => handleAnnouncementsToggle(e.target.checked)}
                        className="checkbox-input"
                        aria-labelledby="announcements-opt-in-label"
                        aria-describedby="announcements-opt-in-help"
                      />
                      <span className="checkbox-custom" aria-hidden="true"></span>
                      <span className="checkbox-text">
                        <strong id="announcements-opt-in-label">Subscribe to Job Seeker Announcements</strong>
                        <small id="announcements-opt-in-help">Receive updates about upcoming career fairs and events</small>
                      </span>
                    </label>
                  </div>
                </fieldset>
                <div className="form-actions form-actions-split registration-final-actions">
                  <button className="ajf-btn ajf-btn-outline" onClick={prev} disabled={isAlreadyRegistered} aria-label="Go to previous step">Previous</button>
                  <button
                    className={`ajf-btn ${isAlreadyRegistered ? 'ajf-btn-disabled' : 'ajf-btn-dark'}`}
                    onClick={handleComplete}
                    disabled={saving || isAlreadyRegistered}
                    aria-label="Complete registration"
                  >
                    {isAlreadyRegistered ? 'Already Registered' : (saving ? 'Completing…' : 'Complete Registration')}
                  </button>
                </div>
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
